/**
 * Wave CF — court forms on a matter: what's attached (and whether a newer edition
 * exists), the case type's packet, and adding forms from the firm's library.
 * Standing React rule: sub-components live at module scope.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Gavel, OpenInNew, PlaylistAdd } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getAuthToken } from '../../lib/authToken';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';

type LibraryForm = { id: string; issuer: string; code: string; title: string; editionDate: string | null; kind: string; enabled: boolean };

async function api(url: string, init?: RequestInit, fallback = 'Request failed') {
  const res = await fetch(url, {
    ...init,
    headers: { Authorization: `Bearer ${getAuthToken()}`, ...(init?.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  if (!res.ok) throw new Error(await getApiErrorMessage(res, fallback));
  return res.json();
}

async function openDocument(documentId: string) {
  try {
    const res = await fetch(`/api/documents/${documentId}/download`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
    if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to open document'));
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    toast.error(toUserErrorMessage(err, 'Unable to open document'));
  }
}

const fmtEdition = (d?: string | null) => {
  if (!d) return 'edition unknown';
  const [y, m, day] = d.split('-');
  return `ed. ${m}/${day}/${y}`;
};

interface AddFormsDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (forms: { issuer: string; code: string }[]) => void;
  saving: boolean;
}

const AddFormsDialog: React.FC<AddFormsDialogProps> = ({ open, onClose, onAdd, saving }) => {
  const [picked, setPicked] = useState<LibraryForm[]>([]);
  const { data, isLoading } = useQuery({
    queryKey: ['court-forms', 'library', 'enabled'],
    enabled: open,
    queryFn: () => api('/api/court-forms/library?enabled=true&kind=form', undefined, 'Unable to load forms'),
    staleTime: 5 * 60 * 1000,
  });
  const forms: LibraryForm[] = data?.data || [];
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add court forms</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Forms your firm has enabled. Each one is copied into this matter&apos;s Court Forms folder as a draft for attorney review.
        </Typography>
        <Autocomplete
          multiple
          loading={isLoading}
          options={forms}
          value={picked}
          onChange={(_, v) => setPicked(v)}
          groupBy={(f) => f.issuer}
          getOptionLabel={(f) => `${f.code} — ${f.title}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => <TextField {...params} label="Search by form number or title" autoFocus />}
        />
        {!isLoading && !forms.length && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No forms are enabled for your firm yet. A firm administrator can choose them under Court Forms → Firm setup.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!picked.length || saving}
          onClick={() => onAdd(picked.map((f) => ({ issuer: f.issuer, code: f.code })))}
        >
          {saving ? 'Adding…' : `Add ${picked.length || ''} form${picked.length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface Props {
  caseId: string;
}

const CaseCourtFormsPanel: React.FC<Props> = ({ caseId }) => {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [chosen, setChosen] = useState<Record<string, boolean>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['case-court-forms', caseId],
    queryFn: () => api(`/api/court-forms/cases/${caseId}`, undefined, 'Unable to load court forms'),
  });
  const attached: any[] = data?.data?.attached || [];
  const packet = data?.data?.packet;
  const attachedCodes = useMemo(() => new Set(attached.map((a) => `${a.issuer}:${a.code}`)), [attached]);

  const attach = useMutation({
    mutationFn: (forms: { issuer: string; code: string }[]) =>
      api(`/api/court-forms/cases/${caseId}/attach`, { method: 'POST', body: JSON.stringify({ forms }) }, 'Unable to add forms'),
    onSuccess: (res) => {
      const r = res.data || {};
      if (r.attached?.length) toast.success(`Added ${r.attached.map((a: any) => a.code).join(', ')}`);
      if (r.skipped?.length) toast(`Already on this matter: ${r.skipped.join(', ')}`, { icon: 'ℹ️' });
      if (r.notInPlan?.length) toast(`Not included in your plan: ${r.notInPlan.join(', ')}`, { icon: '⚠️' });
      if (r.notEnabled?.length) toast(`Not enabled for your firm: ${r.notEnabled.join(', ')}`, { icon: '⚠️' });
      if (r.missing?.length) toast(`Not in the forms library yet: ${r.missing.join(', ')}`, { icon: '⚠️' });
      queryClient.invalidateQueries({ queryKey: ['case-court-forms', caseId] });
      queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] });
      queryClient.invalidateQueries({ queryKey: ['matter-drive'] });
      queryClient.invalidateQueries({ queryKey: ['matter-drive-tree'] });
      setAddOpen(false);
      setChosen({});
    },
    onError: (err: Error) => toast.error(toUserErrorMessage(err, 'Unable to add forms')),
  });

  const packetItems: any[] = packet?.items || [];
  const addable = packetItems.filter((i) => i.inLibrary && i.enabled && !attachedCodes.has(`${i.issuer}:${i.form?.code || i.code}`));
  const selectedPacket = addable.filter((i) => chosen[`${i.issuer}:${i.code}`] ?? i.required);

  if (isLoading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  if (error) return <Alert severity="error">{toUserErrorMessage(error, 'Unable to load court forms')}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Gavel fontSize="small" /> Court forms ({attached.length})
        </Typography>
        <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => setAddOpen(true)}>
          Add from library
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Forms are blank agency editions copied into this matter as <strong>drafts</strong>. An attorney must review them before
        anything is filed or shared with the client.
      </Alert>

      {packet && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {packet.caseType.label} — packet {packet.source === 'firm' ? '(firm)' : '(recommended)'}
          </Typography>
          <Stack spacing={0.5}>
            {packetItems.map((i) => {
              const key = `${i.issuer}:${i.code}`;
              const onMatter = attachedCodes.has(`${i.issuer}:${i.form?.code || i.code}`);
              return (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Checkbox
                    size="small"
                    disabled={onMatter || !i.inLibrary || !i.enabled}
                    checked={onMatter || Boolean(chosen[key] ?? (i.required && i.inLibrary && i.enabled))}
                    onChange={(e) => setChosen((c) => ({ ...c, [key]: e.target.checked }))}
                  />
                  <Typography variant="body2" sx={{ minWidth: 110, fontWeight: 600 }}>{i.form?.code || i.code}</Typography>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 180 }} color={i.inLibrary ? 'text.primary' : 'text.secondary'}>
                    {i.form?.title || 'Not in the forms library yet'}
                  </Typography>
                  {!i.required && <Chip size="small" label={i.condition === 'minor_children' ? 'If minor children' : 'Optional'} variant="outlined" />}
                  {onMatter && <Chip size="small" color="success" label="On matter" />}
                  {i.inLibrary && !i.enabled && <Chip size="small" color="warning" label="Not enabled" />}
                  {!i.inLibrary && <Chip size="small" color="error" variant="outlined" label="Missing from library" />}
                </Box>
              );
            })}
          </Stack>
          <Button
            sx={{ mt: 1.5 }}
            variant="contained"
            size="small"
            startIcon={<PlaylistAdd />}
            disabled={!selectedPacket.length || attach.isPending}
            onClick={() => attach.mutate(selectedPacket.map((i) => ({ issuer: i.issuer, code: i.code })))}
          >
            Add {selectedPacket.length} selected form{selectedPacket.length === 1 ? '' : 's'}
          </Button>
        </Paper>
      )}

      {!packet && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This matter has no court-forms case type. Edit the case and choose a case type to get its recommended packet, or add forms from the library.
        </Alert>
      )}

      {attached.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Form</TableCell>
                <TableCell>Edition</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="right">Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attached.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <strong>{a.code}</strong> <Typography component="span" variant="caption" color="text.secondary">{a.issuer}</Typography>
                  </TableCell>
                  <TableCell>
                    {fmtEdition(a.editionDate)}
                    {a.newerEdition && (
                      <Tooltip title={`A newer edition (${fmtEdition(a.currentEdition)}) is in the library. Agencies may reject outdated editions.`}>
                        <Chip size="small" color="warning" label="Newer edition available" sx={{ ml: 1 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{a.attachedVia === 'auto' ? 'Automatically' : 'Manually'} · {new Date(a.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    {a.documentId && (
                      <Button size="small" endIcon={<OpenInNew fontSize="small" />} onClick={() => openDocument(a.documentId)}>
                        Open
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddFormsDialog open={addOpen} onClose={() => setAddOpen(false)} saving={attach.isPending} onAdd={(forms) => attach.mutate(forms)} />
    </Box>
  );
};

export default CaseCourtFormsPanel;
